<template>
  <div>
    <gg-modal :is-active="show" @close="close">
      <template v-slot:toolbar>
        {{ isActivated ? 'Two-Factor Enabled' : 'Two-factor Enrollment' }}
      </template>
      <template v-slot:body>
        <!-- progress indicator -->
        <v-progress-linear v-if="preloading" color="green" :indeterminate="true"></v-progress-linear>
        <div v-else>
          <!-- list of recovery codes -->
          <div v-if="recovery_codes.length">
            <v-row>
              <v-col cols="12">
                <p>Recovery Codes:</p>
                <p>Store these in a safe place! If you lose access to your 2FA device, using a recovery code to login will disable 2FA protection for your GreenGeeks account.</p>
                <ul>
                  <li v-for="(code, index) in recovery_codes" :key="index">
                    <span>{{ code }}</span>
                    <gg-btn small icon @click="copyToClipboard(code)"><v-icon small text>mdi-content-copy</v-icon></gg-btn>
                  </li>
                </ul>
                <gg-btn text @click="copyToClipboard(recovery_codes)">Copy All</gg-btn>
              </v-col>
              <v-col cols="12">
                <h4 class="subtitle-1 mb-2">Enable Security Questions!</h4>
                <p>
                  We strongly recommend enabling security questions to assist with account recovery in case your
                  authenticator is ever lost. Account recovery options are limited without security questions enabled.
                </p>
                <p class="mb-0">
                  You may now configure security questions at the bottom of the Security section, below the Two-Factor
                  Authentication status.
                </p>
              </v-col>
            </v-row>
          </div>
          <div v-else>
            <!-- form with QR code -->
            <v-row v-if="!isActivated" class="text-center" justify="center">
              <v-col class="align-center text-center" cols="12" sm="4" md="4">
                <vue-qrcode v-if="qrContent" :value="qrContent" :options="{ size: 180 }" />
              </v-col>
            </v-row>

            <v-alert type="error" v-model="showError" dismissible transition="fade-transition">
              {{ error_message }}
            </v-alert>

            <v-row>
              <v-col cols="12">
                <p v-if="!isActivated">
                  Scan the barcode above with your two-factor application, then enter the resulting six digital code.
                </p>
                <p v-else>
                  Enter a valid code from your two-factor application to disable two-factor authentication.
                </p>
              </v-col>
            </v-row>
            <v-form @submit.prevent>
              <gg-text-field outlined
                            v-model="validate_code"
                            required
                            v-validate="'required'"
                            name="Code"
                            :error-messages="errors.collect('Code')"
                            label="Code"
              />
              <v-checkbox v-if="!isActivated"
                          class="font-italic mt-0"
                          v-model="agreement"
                          :label="agreementLabel"></v-checkbox>
            </v-form>
            <div class="error--text" v-if="invalid_code">
              Invalid token. Please try again using a valid recovery code or a code generated
              from your authentication device.
            </div>
          </div>
        </div>
      </template>
      <template v-slot:actions>
        <div v-if="recovery_codes.length">
          <gg-btn primary :disabled="loading" @click="closeSuccess">
            Close
          </gg-btn>
        </div>
        <div v-else>
          <gg-btn secondary :disabled="loading" class="mr-2" @click="close">
            Cancel
          </gg-btn>
          <gg-btn v-if="!isActivated" primary @click="enable2FA" :disabled="!agreement" :loading="loading">
            Enable
          </gg-btn>
          <gg-btn v-else color="red" primary dark :loading="loading" @click="disable2FA">
            Disable
          </gg-btn>

        </div>
      </template>
    </gg-modal>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import VueQrcode from '@xkeshi/vue-qrcode'

export default {
  name: 'TwoFactorAuthModal',
  components: { VueQrcode },
  props: {
    show: {
      type: Boolean,
      required: true,
      default: false
    }
  },
  data () {
    return {
      secret: '',
      validate_code: '',
      error_message: '',
      recovery_codes: [],
      invalid_code: false,
      preloading: true,
      loading: false,
      qrContent: null,
      agreementLabel: 'I understand I will need my authenticator to obtain my support PIN, which is required to ' +
        'receive support for my account. I also understand my account recovery options are limited without my ' +
        'authenticator or answers to my security questions.',
      agreement: false,
    }
  },
  computed: {
    ...mapGetters(['SessionProfile']),
    isActivated () {
      return !!this.SessionProfile.totp_enabled
    },
    // showActions () {
    //   return !this.preloading && !this.recovery_codes
    // },
    // show errors
    showError: {
      get () {
        return !!this.error_message
      },
      set () {
        this.error_message = null
      }
    }
  },
  methods: {
    ...mapActions(['getQRCode', 'activate2FA', 'deactivate2FA', 'getProfile', 'reportSuccess', 'reportError']),
    async requestQRCode () {
      try {
        this.preloading = true

        const { uri } = await this.getQRCode()
        this.preloading = false
        this.qrContent = uri
      } catch (e) {
        this.reportError('Oops! Something went wrong.')
        this.qrContent = null
      }
    },
    async enable2FA () {
      try {
        if (!await this.$validator.validateAll()) return
        this.error_message = null
        this.loading = true

        const { valid, recovery_codes } = await this.activate2FA(this.validate_code)

        if (valid) {
          this.validate_code = ''

          this.recovery_codes = recovery_codes
          await this.getProfile()
          this.loading = false
          this.agreement = false
        } else {
          this.error_message = 'Invalid Code.'
          this.loading = false
        }
      } catch (e) {
        console.log(e)
      }
    },
    async disable2FA () {
      try {
        if (!await this.$validator.validateAll()) return
        this.loading = true
        this.invalid_code = false

        const { valid } = await this.deactivate2FA(this.validate_code)

        if (!valid) {
          this.loading = false
          this.invalid_code = true

          return
        }
        this.close()
        this.reportSuccess('2FA Successfully Disabled')
        await this.getProfile()
        this.requestQRCode()
        this.loading = false
      } catch (e) {
        this.loading = false
        this.reportError(e)
      }
    },
    close () {
      this.$emit('close')
      this.validate_code = ''
      this.error_message = ''
      this.recovery_codes = []
      this.$nextTick(() => {
        this.$validator.reset()
      })
    },
    closeSuccess () {
      this.close()
      this.reportSuccess('Two-factor Authentication is enabled')
    },
    copyToClipboard (value) {
      const type = typeof value
      if (type === 'string') {
        navigator.clipboard.writeText(value)
        this.reportSuccess('Copied')
      } else if (type === 'object' && value.length) {
        navigator.clipboard.writeText(value.join('\n'))
        this.reportSuccess('Copied')
      } else {
        this.reportError('Nothing to copy')
      }
    }
  },
  watch: {
    validate_code () {
      if (this.validate_code) {
        this.error_message = ''
      }
    }
  },
  created () {
    this.requestQRCode()
  }
}
</script>
